import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { tenantService } from '@/services';
import type { Tenant } from '@/services/tenant.service';
import type { TenantFormValues } from '../types';
import { isApiError } from '@core/api/types';

// Re-export for convenience
export type { Tenant };

function toastError(err: unknown, fallback: string) {
  const message = isApiError(err) ? err.message : fallback;
  toast.error(message);
}

/**
 * API-connected tenant CRUD hook.
 *
 * - `loading`    → initial list fetch in progress
 * - `submitting` → mutation (create / update / remove) in progress
 *
 * Each mutation:
 *  - shows a success toast on completion
 *  - shows an error toast and re-throws on failure (so the page can keep
 *    the dialog open)
 *  - remove uses an optimistic update and rolls back on error
 */
export function useTenants(opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled !== false;
  const [tenants, setTenants]     = useState<Tenant[]>([]);
  const [loading, setLoading]     = useState(enabled);
  const [submitting, setSubmitting] = useState(false);

  // ── Initial fetch ─────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await tenantService.list({ limit: 200 });
      setTenants(res.items);
    } catch (err) {
      toastError(err, 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  // ── Create ────────────────────────────────────────────────────────────────

  const create = useCallback(async (values: TenantFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      const tenant = await tenantService.create({
        name: values.name.trim(),
        slug: values.slug.trim().toLowerCase(),
        adminEmail: values.adminEmail.trim().toLowerCase(),
        adminName: values.adminName.trim() || undefined,
      });
      setTenants((prev) => [tenant, ...prev]);
      toast.success(`Tenant "${tenant.name}" created — admin invite sent`);
    } catch (err) {
      toastError(err, 'Failed to create tenant');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // ── Update ────────────────────────────────────────────────────────────────

  const update = useCallback(async (id: string, values: TenantFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      const tenant = await tenantService.update(id, {
        name: values.name.trim(),
        status: values.status,
      });
      setTenants((prev) => prev.map((t) => (t.id === id ? tenant : t)));
      toast.success(`Tenant "${tenant.name}" updated`);
    } catch (err) {
      toastError(err, 'Failed to update tenant');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // ── Remove (optimistic) ───────────────────────────────────────────────────

  const remove = useCallback(async (id: string): Promise<void> => {
    const snapshot = [...([] as Tenant[])]; // captured below
    setTenants((prev) => {
      snapshot.push(...prev);
      return prev.filter((t) => t.id !== id);
    });
    try {
      await tenantService.delete(id);
      toast.success('Tenant deleted');
    } catch (err) {
      setTenants(snapshot); // rollback
      toastError(err, 'Failed to delete tenant');
      throw err;
    }
  }, []);

  return {
    tenants,
    loading,
    submitting,
    create,
    update,
    remove,
    refetch: fetchAll,
  };
}
